import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCourse } from '../contexts/CourseContext';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  BookOpen,
  Plus,
  Trash2,
  Save,
  ArrowLeft,
  Clock,
  Users,
  Award,
  DollarSign,
  Image
} from 'lucide-react';

const CreateCourse = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { createCourse } = useCourse();
  const { getCurrentCurrency } = useCurrency();
  
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnail: '',
    category: 'Développement Web',
    level: 'Débutant',
    language: 'Français',
    price: 0,
    originalPrice: 0,
    duration: '',
    certificate: true,
    access: 'Accès à vie',
    requirements: [''],
    whatYouWillLearn: [''],
    lessons: [
      { id: 1, title: '', duration: '', isFree: true }
    ]
  });

  const [errors, setErrors] = useState({});

  // Vérifier si l'utilisateur est instructeur ou admin
  if (!user || (user.role !== 'instructor' && user.role !== 'admin')) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Accès non autorisé</h1>
          <p className="text-gray-600 mb-6">Vous devez être instructeur ou administrateur pour créer des cours.</p>
          <button 
            onClick={() => navigate('/dashboard')}
            className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
          >
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Effacer l'erreur si l'utilisateur corrige le champ
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleArrayChange = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field, index) => {
    if (formData[field].length > 1) {
      setFormData(prev => ({
        ...prev,
        [field]: prev[field].filter((_, i) => i !== index)
      }));
    }
  };

  const handleLessonChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      lessons: prev.lessons.map((lesson, i) => 
        i === index ? { ...lesson, [field]: value } : lesson
      )
    }));
  };

  const addLesson = () => {
    const newId = Math.max(...formData.lessons.map(l => l.id)) + 1;
    setFormData(prev => ({
      ...prev,
      lessons: [...prev.lessons, { id: newId, title: '', duration: '', isFree: false }]
    }));
  };

  const removeLesson = (index) => {
    if (formData.lessons.length > 1) {
      setFormData(prev => ({
        ...prev,
        lessons: prev.lessons.filter((_, i) => i !== index)
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) newErrors.title = 'Le titre est requis';
    if (!formData.description.trim()) newErrors.description = 'La description est requise';
    if (!formData.duration.trim()) newErrors.duration = 'La durée est requise';
    if (formData.lessons.some(lesson => !lesson.title.trim())) {
      newErrors.lessons = 'Toutes les leçons doivent avoir un titre';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const DIFFICULTY_UNMAP = { 'Débutant': 'beginner', 'Intermédiaire': 'intermediate', 'Avancé': 'advanced' };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const courseData = {
        title:            formData.title,
        description:      formData.description,
        thumbnail:        formData.thumbnail,
        category:         formData.category,
        difficulty:       DIFFICULTY_UNMAP[formData.level] || 'beginner',
        language:         formData.language,
        pricingPrice:     Number(formData.price) || 0,
        estimatedDuration: 0,
        certificate:      formData.certificate,
        access:           formData.access,
        requirements:     formData.requirements.filter(Boolean),
        whatYouWillLearn: formData.whatYouWillLearn.filter(Boolean),
        instructor:       `${user.firstName} ${user.lastName}`,
      };

      const result = await createCourse(courseData);

      if (result.success) {
        navigate(`/instructor/course/${result.course.id}/content/create`);
      } else {
        alert('Erreur lors de la création du cours : ' + result.error);
      }

    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const categories = [
    'Développement Web',
    'Data Science', 
    'Marketing',
    'Design',
    'Business',
    'Langues'
  ];

  const levels = ['Débutant', 'Intermédiaire', 'Avancé'];
  const languages = ['Français', 'Anglais', 'Espagnol'];

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/instructor')}
            className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors duration-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au tableau de bord instructeur
          </button>
          
          <h1 className="text-3xl font-bold text-text mb-2">
            Créer un nouveau cours
          </h1>
          <p className="text-gray-600">
            Remplissez les informations ci-dessous pour créer votre cours.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informations de base */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-text mb-6 flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Informations de base
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre du cours *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Développement Web Full-Stack"
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Niveau
                </label>
                <select
                  name="level"
                  value={formData.level}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {levels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Langue
                </label>
                <select
                  name="language"
                  value={formData.language}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {languages.map(language => (
                    <option key={language} value={language}>{language}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Durée *
                </label>
                <input
                  type="text"
                  name="duration"
                  value={formData.duration}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.duration ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: 12 semaines"
                />
                {errors.duration && <p className="text-red-500 text-sm mt-1">{errors.duration}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Accès
                </label>
                <select
                  name="access"
                  value={formData.access}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="Accès à vie">Accès à vie</option>
                  <option value="1 an">1 an</option>
                  <option value="6 mois">6 mois</option>
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Décrivez votre cours en détail..."
              />
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>
          </div>

          {/* Image de couverture */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-text mb-6 flex items-center gap-2">
              <Image className="w-5 h-5" />
              Image de couverture
            </h2>
            <div className="grid md:grid-cols-2 gap-6 items-start">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  URL de l'image (Cloudinary ou autre)
                </label>
                <input
                  type="url"
                  name="thumbnail"
                  value={formData.thumbnail}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="https://res.cloudinary.com/..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Collez l'URL de l'image depuis Cloudinary ou tout autre hébergeur
                </p>
              </div>
              <div>
                {formData.thumbnail ? (
                  <img
                    src={formData.thumbnail}
                    alt="Aperçu"
                    className="w-full h-40 object-cover rounded-lg border border-gray-200"
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-40 bg-gray-100 rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400">
                    <Image className="w-8 h-8 mb-2" />
                    <span className="text-sm">Aperçu de l'image</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Prix */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-text mb-6 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Tarification
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prix ({getCurrentCurrency().symbol})
                </label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Prix en {getCurrentCurrency().name}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Prix original ({getCurrentCurrency().symbol})
                </label>
                <input
                  type="number"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Prix original en {getCurrentCurrency().name}
                </p>
              </div>
            </div>

            <div className="mt-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="certificate"
                  checked={formData.certificate}
                  onChange={(e) => setFormData(prev => ({ ...prev, certificate: e.target.checked }))}
                  className="w-4 h-4 text-primary"
                />
                <span className="text-sm text-gray-700">Certificat de completion inclus</span>
              </label>
            </div>
          </div>

          {/* Prérequis */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-text mb-6 flex items-center gap-2">
              <Users className="w-5 h-5" />
              Prérequis
            </h2>
            
            <div className="space-y-3">
              {formData.requirements.map((requirement, index) => (
                <div key={index} className="flex gap-3">
                  <input
                    type="text"
                    value={requirement}
                    onChange={(e) => handleArrayChange('requirements', index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Ex: Aucune expérience en programmation requise"
                  />
                  {formData.requirements.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('requirements', index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('requirements')}
                className="flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Ajouter un prérequis
              </button>
            </div>
          </div>

          {/* Ce que vous allez apprendre */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-text mb-6 flex items-center gap-2">
              <Award className="w-5 h-5" />
              Ce que vous allez apprendre
            </h2>
            
            <div className="space-y-3">
              {formData.whatYouWillLearn.map((item, index) => (
                <div key={index} className="flex gap-3">
                  <input
                    type="text"
                    value={item}
                    onChange={(e) => handleArrayChange('whatYouWillLearn', index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Ex: Créer des sites web responsifs avec HTML, CSS et JavaScript"
                  />
                  {formData.whatYouWillLearn.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeArrayItem('whatYouWillLearn', index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem('whatYouWillLearn')}
                className="flex items-center gap-2 text-primary hover:text-primary/80 text-sm font-medium"
              >
                <Plus className="w-4 h-4" />
                Ajouter un objectif d'apprentissage
              </button>
            </div>
          </div>

          {/* Leçons */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-xl font-bold text-text mb-6 flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Programme du cours
            </h2>
            
            <div className="space-y-4">
              {formData.lessons.map((lesson, index) => (
                <div key={lesson.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Titre de la leçon *
                      </label>
                      <input
                        type="text"
                        value={lesson.title}
                        onChange={(e) => handleLessonChange(index, 'title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Ex: Introduction au développement web"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Durée
                      </label>
                      <input
                        type="text"
                        value={lesson.duration}
                        onChange={(e) => handleLessonChange(index, 'duration', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder="Ex: 15 min"
                      />
                    </div>
                  </div>
                  
                  <div className="mt-4 flex items-center justify-between">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={lesson.isFree}
                        onChange={(e) => handleLessonChange(index, 'isFree', e.target.checked)}
                        className="w-4 h-4 text-primary"
                      />
                      <span className="text-sm text-gray-700">Leçon gratuite</span>
                    </label>
                    
                    {formData.lessons.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLesson(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              
              <button
                type="button"
                onClick={addLesson}
                className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-primary hover:text-primary transition-colors duration-200"
              >
                <Plus className="w-5 h-5" />
                Ajouter une leçon
              </button>
            </div>
            
            {errors.lessons && <p className="text-red-500 text-sm mt-2">{errors.lessons}</p>}
          </div>

          {/* Actions */}
          <div className="flex gap-4 justify-end">
            <button
              type="button"
              onClick={() => navigate('/instructor')}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex items-center gap-2 bg-primary text-white px-6 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Création en cours...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Créer le cours
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCourse;
